import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";

// Components
import TextInput from "new_components/molecules/TextInput";
import ToggleItem from "new_components/molecules/ToggleItem";
import ImageButton from "new_components/molecules/ImageButton";
import SelectBox from "new_components/molecules/SelectBox";
import Text from "new_components/atoms/Text"; // Import the Text component
import Tooltip from "new_components/molecules/Tooltip";

// Constants
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import typography from "styles/typography";

const allowQuantitative = true;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryQuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${colors.background.lightBlue};
  padding: 16px;
  border-radius: 8px;
`;

const QuestionText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text.dark};
  line-height: 1.4;
`;

const QuestionMeta = styled.div`
  font-size: 14px;
  color: ${colors.text.gray};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const QuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${colors.background.lightBlue};
  padding: 16px;
  border-radius: 8px;
`;

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  display: ${({ noEdit }) => (noEdit ? 'none' : 'flex')};
`;

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-top: 20px;
`;

const MetadataRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 16px;
  padding: 12px;
  background-color: ${colors.background.white};
  border-radius: 6px;
  border: 1px solid ${colors.neutral[500]};
`;

const MetadataField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 120px;
  
  /* Ensure proper spacing for input labels */
  & > div {
    position: relative;
  }
  
  /* Override any potential label positioning issues */
  & label {
    margin-bottom: 4px;
    display: block;
  }
`;

const QualifierInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const Qualifier = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 25%;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const OptionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TypeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;

const SurveyQuestionSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: ${colors.background.white};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const Questions = ({ questions, editable, onUpdate, errors, noEdit = false }) => {

  const get_survey_questions = useSelector((state) => state.surveyquestions);
  
  // Combine editable and noEdit - component is editable only if editable is true AND noEdit is false
  const isEditable = editable !== false && !noEdit;

  // Extract CAAT v3 survey questions and comments
  const getCaatQuestions = () => {
    const caatSurvey = get_survey_questions?.survey_questions?.find(survey => survey.id === 178);
    if (!caatSurvey) return [];

    const surveyQuestions = [];
    
    // Extract questions from dimensions/factors
    if (caatSurvey.questions?.dimensions) {
      caatSurvey.questions.dimensions.forEach((dimension, dimIndex) => {
        dimension.factors.forEach((factor, factorIndex) => {
          // Add factor header
          surveyQuestions.push({
            id: `factor_header_${dimension.id}_${factor.id}`,
            label: `── ${dimension.title} / ${factor.title} ──`,
            value: `factor_header_${dimension.id}_${factor.id}`,
            type: 'header',
            isHeader: true
          });

          factor.questions.forEach((question, questionIndex) => {
            surveyQuestions.push({
              id: question.id,
              label: `   ${question.q}`,
              value: `question_${question.id}`,
              type: 'question',
              data: {
                question: question.q,
                questionFrench: question.fr,
                dimension: dimIndex, // Use index instead of dimension.id
                factor: factor.id,
                q_id: questionIndex, // Use index within factor instead of question.id
                binary: question.binary || false,
                reverse: question.reverse || false,
                role: question.role || null
              }
            });
          });
        });
      });
    }

    // Add comments section
    if (caatSurvey.questions?.comments && caatSurvey.questions.comments.length > 0) {
      // Add comments header
      surveyQuestions.push({
        id: 'comments_header',
        label: '── Comments ──',
        value: 'comments_header',
        type: 'header',
        isHeader: true
      });

      caatSurvey.questions.comments.forEach((comment, commentIndex) => {
        surveyQuestions.push({
          id: comment.id,
          label: `   ${comment.question}`,
          value: `comment_${comment.id}`,
          type: 'comment',
          data: {
            question: comment.question,
            questionFrench: comment.fr,
            dimension: null,
            factor: null,
            q_id: commentIndex, // Use index within comments
            binary: comment.binary || false,
            reverse: false,
            role: comment.role || null,
            order: comment.order
          }
        });
      });
    }

    return surveyQuestions;
  };

  // Extract CAAT v3 factors for bulk addition
  const getCaatFactors = () => {
    const caatSurvey = get_survey_questions?.survey_questions?.find(survey => survey.id === 178);
    if (!caatSurvey) return [];

    const factors = [];
    
    // Extract factors from dimensions
    if (caatSurvey.questions?.dimensions) {
      caatSurvey.questions.dimensions.forEach((dimension, dimIndex) => {
        dimension.factors.forEach((factor, factorIndex) => {
          factors.push({
            id: `factor_${dimension.id}_${factor.id}`,
            label: `${dimension.title} / ${factor.title} (${factor.questions.length} questions)`,
            value: `factor_${dimension.id}_${factor.id}`,
            data: {
              dimension: dimIndex,
              factor: factor,
              dimensionTitle: dimension.title,
              factorTitle: factor.title
            }
          });
        });
      });
    }

    return factors;
  };

  const caatQuestions = getCaatQuestions();
  const caatFactors = getCaatFactors();

  // Summary view for read-only mode
  const renderSummaryView = () => {
    return (
      <SummaryWrapper>
        <Text
          typographyStyle={typography.body.medium}
          color={colors.text.dark}
        >
          {questions.length} Question{questions.length !== 1 ? 's' : ''} in this survey:
        </Text>
        {questions.map((question, index) => (
          <SummaryQuestionWrapper key={question.id || index}>
            <QuestionText>{question.question}</QuestionText>
            <QuestionMeta>
              <span>Question #{index + 1}</span>
              <span>•</span>
              <span>Type: {
                typeof question.type === 'string' 
                  ? question.type.charAt(0).toUpperCase() + question.type.slice(1)
                  : question.type?.label || 'Text'
              }</span>
              {question.scale && (
                <>
                  <span>•</span>
                  <span>Scale: {question.scale}</span>
                </>
              )}
            </QuestionMeta>
          </SummaryQuestionWrapper>
        ))}
      </SummaryWrapper>
    );
  };

  const handleAddCaatQuestion = (selectedValue) => {
    if (!isEditable || !selectedValue) return;
    
    const selectedQuestion = caatQuestions.find(q => q.value === selectedValue);
    if (!selectedQuestion || selectedQuestion.isHeader) return;

    const isComment = selectedQuestion.type === 'comment';
    const isBinary = selectedQuestion.data.binary && !isComment;

    const newQuestion = {
      id: uuidv4(),
      question: selectedQuestion.data.question,
      questionFrench: selectedQuestion.data.questionFrench,
      aiEnhanced: false,
      task_type: "question",
      type: isComment ? "text" : (isBinary ? "single" : "scale"),
      scale: isComment || isBinary ? null : "0-10",
      qualifier: isComment || isBinary ? null : ["Strongly Disagree", "Neutral", "Strongly Agree"],
      options: isBinary ? ["Yes", "No"] : null,
      meta_data: {
        dimension: selectedQuestion.data.dimension,
        factor: selectedQuestion.data.factor,
        q_id: selectedQuestion.data.q_id,
      },
    };

    const updatedQuestions = [...questions, newQuestion];
    onUpdate(updatedQuestions);
  };

  const handleAddCaatFactor = (selectedValue) => {
    if (!isEditable || !selectedValue) return;
    
    const selectedFactor = caatFactors.find(f => f.value === selectedValue);
    if (!selectedFactor) return;

    const factor = selectedFactor.data.factor;
    const dimension = selectedFactor.data.dimension;
    
    // Create questions for all questions in the factor
    const newQuestions = factor.questions.map((question, questionIndex) => {
      const isBinary = question.binary || false;

      return {
        id: uuidv4(),
        question: question.q,
        questionFrench: question.fr,
        aiEnhanced: false,
        task_type: "question",
        type: isBinary ? "single" : "scale",
        scale: isBinary ? null : "0-10",
        qualifier: isBinary ? null : ["Strongly Disagree", "Neutral", "Strongly Agree"],
        options: isBinary ? ["Yes", "No"] : null,
        meta_data: {
          dimension: dimension,
          factor: factor.id,
          q_id: questionIndex,
        },
      };
    });

    const updatedQuestions = [...questions, ...newQuestions];
    onUpdate(updatedQuestions);
  };

  const handleQuestionChange = (index, field, value) => {
    if (!isEditable) return;
    const updatedQuestions = [...questions];
    // If the field is type, and its value is quantifier default the first option to "0-10"
    if (field === "type" && value === "scale") {
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value,
        scale: "0-10",
      };
    } else if (field === "type" && (value === "multi" || value === "single")) {
      // Preserve existing options if they exist, otherwise initialize with one empty option
      const existingOptions = updatedQuestions[index].options || [""];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value,
        options: existingOptions,
      };
    } else {
      updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    }
    onUpdate(updatedQuestions);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    if (!isEditable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const options = [...(question.options || [""])];
    options[optionIndex] = value;
    updatedQuestions[questionIndex].options = options;
    onUpdate(updatedQuestions);
  };

  const handleAddOption = (questionIndex) => {
    if (!isEditable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const options = [...(question.options || []), ""];
    updatedQuestions[questionIndex].options = options;
    onUpdate(updatedQuestions);
  };

  const handleRemoveOption = (questionIndex, optionIndex) => {
    if (!isEditable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const options = question.options.filter((_, index) => index !== optionIndex);
    updatedQuestions[questionIndex].options = options;
    onUpdate(updatedQuestions);
  };

  const handleQualifierChange = (index, position, value) => {
    if (!isEditable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[index];

    // If scaleOptions is present, don't allow changes
    if (question.scaleOptions) {
      return;
    }

    const currentQualifiers = question.qualifier || [
      "Strongly Disagree",
      "Neutral",
      "Strongly Agree",
    ];
    const newQualifiers = [...currentQualifiers];
    // position: 0 for minimum, 1 for middle, 2 for maximum
    newQualifiers[position] = value;
    updatedQuestions[index].qualifier = newQualifiers;
    onUpdate(updatedQuestions);
  };

  const handleMetadataChange = (index, field, value) => {
    if (!isEditable) return;
    const updatedQuestions = [...questions];
    const currentMetadata = updatedQuestions[index].meta_data || {};
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      meta_data: {
        ...currentMetadata,
        [field]: value === "" ? null : parseInt(value, 10),
      },
    };
    onUpdate(updatedQuestions);
  };

  const handleRemoveQuestion = (index) => {
    if (!isEditable) return;
    const updatedQuestions = questions.filter((_, i) => i !== index);
    onUpdate(updatedQuestions);
  };

  const handleAddQuestion = () => {
    if (!isEditable) return;
    const updatedQuestions = [
      ...questions,
      {
        id: uuidv4(),
        question: "",
        questionFrench: "",
        aiEnhanced: false,
        task_type: "question",
        type: "text",
        scale: null,
        qualifier: null,
        options: null,
        meta_data: {
          dimension: null,
          factor: null,
          q_id: null,
        },
      },
    ];
    onUpdate(updatedQuestions);
  };

  const handleMoveQuestionUp = (index) => {
    if (!isEditable || index === 0) return;
    const updatedQuestions = [...questions];
    [updatedQuestions[index - 1], updatedQuestions[index]] = [updatedQuestions[index], updatedQuestions[index - 1]];
    onUpdate(updatedQuestions);
  };

  const handleMoveQuestionDown = (index) => {
    if (!isEditable || index === questions.length - 1) return;
    const updatedQuestions = [...questions];
    [updatedQuestions[index], updatedQuestions[index + 1]] = [updatedQuestions[index + 1], updatedQuestions[index]];
    onUpdate(updatedQuestions);
  };

  // If not editable, show summary view
  if (!isEditable) {
    return renderSummaryView();
  }

  return (
    <SectionWrapper>
      {isEditable && caatFactors.length > 0 && (
        <SurveyQuestionSelector>
          <Text
            typographyStyle={typography.caption.semibold}
            color={colors.text.dark}
          >
            Add All Questions from CAAT v3 Factor:
          </Text>
          <SelectBox
            width="100%"
            label="Select a factor to add all questions"
            options={caatFactors.map(f => ({
              value: f.value,
              label: f.label
            }))}
            onSelect={handleAddCaatFactor}
            placeholder="Choose a factor from CAAT v3..."
          />
        </SurveyQuestionSelector>
      )}

              {isEditable && caatQuestions.length > 0 && (
        <SurveyQuestionSelector>
          <Text
            typographyStyle={typography.caption.semibold}
            color={colors.text.dark}
          >
            Add Individual Question from CAAT v3 Survey:
          </Text>
          <SelectBox
            width="100%"
            label="Select a question to add"
            options={caatQuestions.map(q => ({
              value: q.value,
              label: q.label
            }))}
            onSelect={handleAddCaatQuestion}
            placeholder="Choose a question from CAAT v3..."
          />
        </SurveyQuestionSelector>
      )}

      {questions.map((question, index) => (
        <QuestionWrapper key={question.id || index}>
          {/* Question Text */}
          <TextInput
            label={`Question #${index + 1}`}
            value={question.question}
            onChange={(e) => handleQuestionChange(index, "question", e)}
            placeholder="Enter your question"
            readOnly={!isEditable}
            error={errors?.surveyQuestionValidation && question.question === ""}
          />

          {/* French Question Text */}
          <TextInput
            label={`Question #${index + 1} (French)`}
            value={question.questionFrench || ""}
            onChange={(e) => handleQuestionChange(index, "questionFrench", e)}
            placeholder="Enter your question in French"
            readOnly={!isEditable}
          />

          {/* Metadata Fields */}
          <MetadataRow>
            <Text
              typographyStyle={typography.caption.semibold}
              color={colors.text.dark}
            >
              Metadata:
            </Text>
            <MetadataField>
              <TextInput
                label="Dimension"
                type="number"
                value={question.meta_data?.dimension !== null && question.meta_data?.dimension !== undefined ? question.meta_data.dimension.toString() : ""}
                onChange={(e) => handleMetadataChange(index, "dimension", e)}
                placeholder="0"
                readOnly={!isEditable}
                width="120px"
              />
            </MetadataField>
            <MetadataField>
              <TextInput
                label="Factor"
                type="number"
                value={question.meta_data?.factor !== null && question.meta_data?.factor !== undefined ? question.meta_data.factor.toString() : ""}
                onChange={(e) => handleMetadataChange(index, "factor", e)}
                placeholder="0"
                readOnly={!isEditable}
                width="120px"
              />
            </MetadataField>
            <MetadataField>
              <TextInput
                label="Q ID"
                type="number"
                value={question.meta_data?.q_id !== null && question.meta_data?.q_id !== undefined ? question.meta_data.q_id.toString() : ""}
                onChange={(e) => handleMetadataChange(index, "q_id", e)}
                placeholder="0"
                readOnly={!isEditable}
                width="120px"
              />
            </MetadataField>
          </MetadataRow>

          <TypeRow>
            {allowQuantitative && (
              <FieldRow>
                <SelectBox
                  width={"220px"}
                  label="Question Type"
                  readOnly={!isEditable}
                  value={question.type}
                  onSelect={(value) =>
                    handleQuestionChange(index, "type", value)
                  }
                  disabled={!isEditable}
                  options={[
                    { value: "text", label: "Open Text", icon: ICON_SET.pulseText },
                    { value: "scale", label: "Sliding Scale", icon: ICON_SET.pulseScale },
                    { value: "multi", label: "Multiple Answer", icon: ICON_SET.pulseMulti },
                    { value: "single", label: "Multiple Choice", icon: ICON_SET.pulseSingle },
                  ]}
                  placeholder="Select type"
                />
              </FieldRow>
            )}

            {question.type === "scale" && (
              <FieldRow>
                <SelectBox
                  label="Scale"
                  readOnly={!isEditable}
                  width={"200px"}
                  options={[
                    { value: "0-10", label: "10 Point" },
                    { value: "0-5", label: "5 Point" },
                  ].map((option) => {
                    return {
                      value: option.value,
                      label: option.label,
                    };
                  })}
                  placeholder="Select scale"
                  value={question.scale}
                  onSelect={(value) =>
                    handleQuestionChange(index, "scale", value)
                  }
                  disabled={!isEditable}
                />
              </FieldRow>
            )}
          </TypeRow>

          {question.type === "scale" && (
            <FieldRow>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                Qualifiers:
              </Text>
              <QualifierInputWrapper>
                <Qualifier>
                  <TextInput
                    placeholder="Strongly Disagree"
                    readOnly={!isEditable}
                    value={
                      question.scaleOptions
                        ? question.scaleOptions[0]
                        : question.qualifier
                        ? question.qualifier[0]
                        : "Strongly Disagree"
                    }
                    onChange={(e) => handleQualifierChange(index, 0, e)}
                    label="Minimum"
                    disabled={!isEditable || question.scaleOptions}
                  />
                </Qualifier>
                <Qualifier>
                  <TextInput
                    placeholder="Neutral"
                    readOnly={!isEditable}
                    value={
                      question.scaleOptions
                        ? question.scaleOptions[Math.floor(question.scaleOptions.length / 2)]
                        : question.qualifier
                          ? question.qualifier[1]
                          : "Neutral"
                    }
                    onChange={(e) => handleQualifierChange(index, 1, e)}
                    label="Middle"
                    disabled={!isEditable || question.scaleOptions}
                  />
                </Qualifier>
                <Qualifier>
                  <TextInput
                    placeholder="Strongly Agree"
                    readOnly={!isEditable}
                    value={
                      question.scaleOptions
                        ? question.scaleOptions[question.scaleOptions.length - 1]
                        : question.qualifier
                        ? question.qualifier[2]
                        : "Strongly Agree"
                    }
                    onChange={(e) => handleQualifierChange(index, 2, e)}
                    label="End"
                    disabled={!isEditable || question.scaleOptions}
                  />
                </Qualifier>
              </QualifierInputWrapper>
            </FieldRow>
          )}

          {(question.type === "multi" || question.type === "single") && (
            <OptionsContainer>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                Options:
              </Text>
              {(question.options || [""]).map((option, optionIndex) => (
                <OptionRow key={optionIndex}>
                  <TextInput
                    value={option || `Option ${optionIndex + 1}`}

                    onChange={(e) => handleOptionChange(index, optionIndex, e)}
                    readOnly={!isEditable}
                    width="100%"
                  />
                  {isEditable && question.options?.length > 1 && (
                    <ImageButton
                      icon={ICON_SET.delete}
                      textStyle={true}
                      color={colors.auxiliary.red[500]}
                      onClick={() => handleRemoveOption(index, optionIndex)}
                    />
                  )}
                </OptionRow>
              ))}
              {isEditable && (
                <ImageButton
                  text="Add option"
                  icon={ICON_SET.add}
                  iconPosition="left"
                  textStyle={true}
                  onClick={() => handleAddOption(index)}
                  color={colors.primary[500]}
                />
              )}
            </OptionsContainer>
          )}

          <OptionsRow noEdit={!isEditable}>
            {isEditable && (
              <>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Tooltip content="Move question up">
                    <ImageButton
                      icon={ICON_SET.arrowUp}
                      textStyle={true}
                      color={index === 0 ? colors.neutral[400] : colors.neutral[700]}
                      onClick={() => handleMoveQuestionUp(index)}
                      disabled={index === 0}
                      aria-label="Move question up"
                    />
                  </Tooltip>
                  <Tooltip content="Move question down">
                    <ImageButton
                      icon={ICON_SET.arrowDown}
                      textStyle={true}
                      color={index === questions.length - 1 ? colors.neutral[400] : colors.neutral[700]}
                      onClick={() => handleMoveQuestionDown(index)}
                      disabled={index === questions.length - 1}
                      aria-label="Move question down"
                    />
                  </Tooltip>
                </div>
                <ImageButton
                  text="Remove question"
                  icon={ICON_SET.delete}
                  iconPosition="left"
                  textStyle={true}
                  color={colors.auxiliary.red[500]}
                  onClick={() => handleRemoveQuestion(index)}
                />
              </>
            )}
          </OptionsRow>
        </QuestionWrapper>
      ))}

      {isEditable && (
        <ImageButton
          text="Add question"
          icon={ICON_SET.add}
          iconPosition="left"
          textStyle={true}
          onClick={handleAddQuestion}
          color={colors.primary[500]}
        />
      )}
    </SectionWrapper>
  );
};

Questions.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      questionFrench: PropTypes.string,
      aiEnhanced: PropTypes.bool.isRequired,
      task_type: PropTypes.string.isRequired,
      type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        }),
      ]),
      scale: PropTypes.oneOf(["0-10", "1-5", "0-5", null]),
      qualifier: PropTypes.arrayOf(PropTypes.string),
      scaleOptions: PropTypes.arrayOf(PropTypes.string),
      options: PropTypes.arrayOf(PropTypes.string),
      meta_data: PropTypes.shape({
        dimension: PropTypes.number,
        factor: PropTypes.number,
        q_id: PropTypes.number,
      }),
    })
  ).isRequired,
  editable: PropTypes.bool,
  onUpdate: PropTypes.func,
};

Questions.defaultProps = {
  editable: true, // Default to editable
  onUpdate: () => {}, // Default no-op function
};

export default Questions;
